import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { wabaApi } from "../../lib/wabaApi";
import { api } from "../../lib/api";
import { SendBehaviorToggles } from "../waba/SendBehaviorToggles";
import { DialogPanel } from "../shared/DialogPanel";
import { XIcon, CameraIcon, MessageIcon, TagIcon, CartIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";

const MAX_WEBHOOKS = 5;

function _apiError(err: any, fallback: string): string {
  const detail = err?.payload?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e: any) => e.msg ?? String(e)).join("; ");
  return fallback;
}

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const OTHER_CHANNELS = [
  { label: "Instagram", Icon: CameraIcon },
  { label: "Facebook Messenger", Icon: MessageIcon },
  { label: "OLX", Icon: TagIcon },
  { label: "Mercado Livre", Icon: CartIcon },
];

function ConfirmModal({
  title,
  body,
  confirmLabel,
  loading,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div {...backdropClose(() => { onCancel(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
      <DialogPanel onClose={() => onCancel()} role="alertdialog" label={title} style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, padding: 28, maxWidth: 400, width: "90%" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 13, color: "var(--ink-secondary)", margin: "0 0 20px" }}>{body}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{ flex: 1, background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}
          >
            {loading ? "Aguarde..." : confirmLabel}
          </button>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{ flex: 1, background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}
          >
            Cancelar
          </button>
        </div>
      </DialogPanel>
    </div>
  );
}

function CustomWebhooks({ agentId }: { agentId: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data: webhooks = [] } = useQuery<any[]>({
    queryKey: ["webhooks", agentId],
    queryFn: () => api.get(`/dev/webhooks?agent_id=${agentId}`),
  });

  const createMut = useMutation({
    mutationFn: () => api.post("/dev/webhooks", { agent_id: agentId, name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhooks", agentId] });
      setName("");
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/dev/webhooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks", agentId] }),
  });

  function copyUrl(id: string, url: string) {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {webhooks.map((w: any) => (
        <div key={w.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 11, color: "var(--ink-secondary)" }}>{w.name}</span>
            <code style={{ minWidth: 0, fontSize: 11, padding: "4px 8px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, color: "var(--ink-soft)", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {w.url}
            </code>
          </div>
          <button onClick={() => copyUrl(w.id, w.url)} style={{ background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, color: copiedId === w.id ? "var(--ok)" : "var(--ink-secondary)", fontSize: 12, padding: "5px 10px", cursor: "pointer", flexShrink: 0 }}>
            {copiedId === w.id ? "Copiado" : "Copiar"}
          </button>
          <button aria-label="Remover" onClick={() => deleteMut.mutate(w.id)} style={{ background: "transparent", border: "none", color: "var(--brand)", fontSize: 14, cursor: "pointer", padding: "4px 6px", flexShrink: 0 }}>
            <XIcon size={13} />
          </button>
        </div>
      ))}

      {webhooks.length < MAX_WEBHOOKS && (
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <input
            style={{ flex: 1, padding: "7px 10px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, color: "var(--ink)", fontSize: 12, outline: "none" }}
            placeholder="nome do webhook"
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-"))}
          />
          <button onClick={() => createMut.mutate()} disabled={!name || createMut.isPending} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>
            + Criar
          </button>
        </div>
      )}

      <span style={{ fontSize: 11, color: "var(--line-max)" }}>{webhooks.length}/{MAX_WEBHOOKS} webhooks utilizados</span>
    </div>
  );
}

function WabaSection({ agentId, agentName }: { agentId: string; agentName: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [selected, setSelected] = useState("");
  const [connSearch, setConnSearch] = useState("");
  const [confirmRelease, setConfirmRelease] = useState(false);

  const inputStyle: React.CSSProperties = {
    padding: "8px 12px",
    background: "var(--surface-input)",
    border: "1px solid var(--line)",
    borderRadius: 8,
    color: "var(--ink)",
    fontSize: 13,
    outline: "none",
    width: "100%",
  };

  const { data: available = [] } = useQuery<any[]>({
    queryKey: ["waba-available", agentId],
    queryFn: () => wabaApi.get(`/bindings/available?agent_id=${agentId}`),
  });

  const { data: bound } = useQuery<any | null>({
    queryKey: ["waba-bound", agentId],
    queryFn: () => wabaApi.get(`/bindings/agent/${agentId}`),
  });

  const claimMut = useMutation({
    mutationFn: () => wabaApi.post(`/bindings/claim`, { connection_id: selected, agent_id: agentId, agent_name: agentName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waba-bound", agentId] });
      qc.invalidateQueries({ queryKey: ["waba-available", agentId] });
      setSelected("");
    },
  });

  const releaseMut = useMutation({
    mutationFn: () => wabaApi.delete(`/bindings/agent/${agentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waba-bound", agentId] });
      qc.invalidateQueries({ queryKey: ["waba-available", agentId] });
      setConfirmRelease(false);
    },
  });

  if (bound) {
    return (
      <>
        {confirmRelease && (
          <ConfirmModal
            title="Desvincular conexão?"
            body={<>A conexão <strong style={{ color: "var(--ink)" }}>{bound.name}</strong> será desvinculada deste agente. O número continua ativo na aba WABA.</>}
            confirmLabel="Confirmar"
            loading={releaseMut.isPending}
            onConfirm={() => releaseMut.mutate()}
            onCancel={() => setConfirmRelease(false)}
          />
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={labelStyle}>Conexão vinculada</label>
            <div style={{ flex: 1, padding: "10px 12px", background: "var(--ok-soft)", border: "1px solid var(--ok-line)", borderRadius: 8 }}>
              <div style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600 }}>{bound.name}</div>
              <div style={{ fontSize: 12, color: "var(--ok)", marginTop: 2 }}>
                {bound.phone_number ?? "—"} · {bound.display_name ?? "Ativo"}
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 6, fontFamily: "var(--font-mono)" }}>
                <span style={{ color: "var(--line-max)" }}>instance: </span>{bound.instance_id}
              </div>
            </div>
          </div>

          <SendBehaviorToggles
            conn={bound}
            onChanged={() => qc.invalidateQueries({ queryKey: ["waba-bound", agentId] })}
          />

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => navigate({ to: "/waba", search: { open: bound.id } })}
              style={{ background: "var(--surface-input)", color: "var(--ink)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "7px 18px", fontSize: 13, cursor: "pointer" }}
            >
              Editar na WABA
            </button>
            <button
              onClick={() => setConfirmRelease(true)}
              style={{ background: "transparent", color: "var(--brand)", border: "1px solid var(--brand-line)", borderRadius: 8, padding: "7px 18px", fontSize: 13, cursor: "pointer" }}
            >
              Desvincular
            </button>
          </div>
        </div>
      </>
    );
  }

  const q = connSearch.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, "");
  const filteredConns = available.filter((c) => {
    if (!q) return true;
    return (
      (c.name ?? "").toLowerCase().includes(q) ||
      (c.display_name ?? "").toLowerCase().includes(q) ||
      (qDigits !== "" && ((c.phone_number ?? "").replace(/\D/g, "").includes(qDigits)))
    );
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <label style={labelStyle}>Selecione uma conexão WABA ativa</label>
        <input
          style={inputStyle}
          placeholder="Buscar por nome ou telefone..."
          value={connSearch}
          onChange={(e) => setConnSearch(e.target.value)}
        />
        <select style={{ ...inputStyle, cursor: "pointer" }} value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="">— escolher conexão —</option>
          {filteredConns.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.phone_number ?? "sem número"})</option>
          ))}
        </select>
        {q && filteredConns.length === 0 && available.length > 0 && (
          <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>Nenhuma conexão bate com "{connSearch}".</span>
        )}
      </div>
      {available.length === 0 && (
        <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
          Nenhuma conexão ativa disponível. Crie e vincule um número na aba WABA primeiro.
        </span>
      )}
      <button
        onClick={() => claimMut.mutate()}
        disabled={!selected || claimMut.isPending}
        style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: "pointer", alignSelf: "flex-start" }}
      >
        {claimMut.isPending ? "Vinculando..." : "Vincular ao agente"}
      </button>
      {claimMut.isError && (
        <span style={{ fontSize: 12, color: "var(--danger)" }}>
          {_apiError(claimMut.error, "Erro ao vincular.")}
        </span>
      )}
    </div>
  );
}

export function TabConexao({ agent }: { agent: any }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, padding: 24 }}>

      {/* WABA Carmillion */}
      <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: 20, maxWidth: 520 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Carmillion (WABA)</span>
        </div>
        <WabaSection agentId={agent.id} agentName={agent.name ?? "Agente"} />
      </div>

      {/* Webhooks customizáveis */}
      <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: 20, maxWidth: 520 }}>
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Webhooks customizáveis</span>
          <p style={{ fontSize: 12, color: "var(--ink-muted)", margin: "4px 0 0" }}>
            URLs públicas que disparam este agente externamente. Compartilhe com CRMs, formulários, etc.
          </p>
        </div>
        <CustomWebhooks agentId={agent.id} />
      </div>

      {/* Outros canais */}
      <div style={{ maxWidth: 520 }}>
        <span style={{ fontSize: 13, color: "var(--ink-muted)", display: "block", marginBottom: 12 }}>Outros canais</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {OTHER_CHANNELS.map((ch) => (
            <div key={ch.label} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ display: "flex", color: "var(--ink-muted)" }}><ch.Icon size={16} /></span>
                <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>{ch.label}</span>
              </div>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-input)", border: "1px solid var(--line-strong)", color: "var(--line-max)" }}>
                Em breve
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
